/**
 * SavedProCalculation API Service
 *
 * Type-safe API functions for professional calculator saved calculation operations.
 *
 * Business Rules:
 * - ONE calculation per user per year
 * - Users UPDATE existing calculations, don't create duplicates
 * - Always check for existing calculation before creating
 */

import { getCurrentUser } from 'aws-amplify/auth';
import { getAmplifyClient } from '../amplifyClient';
import type {
  ProCalculatorFormValues,
  ProCalculatorResults,
  MonthlyConfiguration,
} from '../../types/proCalculator';

// Lazy client getter - ensures Amplify is configured before accessing models
const client = {
  get models() {
    return getAmplifyClient().models;
  }
};

/**
 * SavedProCalculation Input Types
 */
export interface CreateSavedProCalculationInput {
  userId: string;
  calculationYear: number;
  distributor: string;
  monthlyConsumption: any; // JSON array[12] of kWh values
  tariffPerMonth: any; // JSON object { january: "G11", february: "G12", ... }
  hourlyPatternPerMonth: any; // JSON object { january: "UNIFORM", ... }
  valuesFromBasic: any; // JSON - (a) values from SavedCalculation or tariff pricing
  valuesFromInvoice: any; // JSON - (b) values from PDF import (Phase 2 - always null in MVP)
  valuesOverridden: any; // JSON - (c) values manually entered by user
  valuesFinal: any; // JSON - (d) values used for calculations (c → b → a → 0)
  simulationResults: any; // JSON - all 6 tariff simulations
  recommendedTariff: string; // "G11", "G12", etc.
  potentialSavings: number; // PLN/year
  savedCalculationId?: string; // Reference to SavedCalculation (optional)
  version: number; // Start at 1
}

export interface UpdateSavedProCalculationInput {
  id: string;
  calculationYear?: number;
  distributor?: string;
  monthlyConsumption?: any;
  tariffPerMonth?: any;
  hourlyPatternPerMonth?: any;
  valuesFromBasic?: any;
  valuesFromInvoice?: any;
  valuesOverridden?: any;
  valuesFinal?: any;
  simulationResults?: any;
  recommendedTariff?: string;
  potentialSavings?: number;
  savedCalculationId?: string;
  version: number; // Required for optimistic locking
}

/**
 * SavedProCalculation Response Type
 */
export interface SavedProCalculation {
  id: string;
  userId: string;
  calculationYear: number;
  distributor: string;
  monthlyConsumption: any;
  tariffPerMonth: any;
  hourlyPatternPerMonth: any;
  valuesFromBasic: any;
  valuesFromInvoice: any;
  valuesOverridden: any;
  valuesFinal: any;
  simulationResults: any;
  recommendedTariff: string;
  potentialSavings: number;
  savedCalculationId?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  owner: string;
}

/**
 * Get current authenticated user ID
 */
async function getCurrentUserId(): Promise<string> {
  try {
    const user = await getCurrentUser();
    return user.userId;
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedProCalculation] getCurrentUserId error:', error);
    }
    throw new Error('User not authenticated');
  }
}

/**
 * Check if user has a saved professional calculation for a specific year
 *
 * CRITICAL: Always call this before creating a new saved calculation
 * to enforce one-per-year constraint
 *
 * @param userId - User ID
 * @param year - Calculation year (e.g., 2025)
 * @returns Existing calculation or null
 */
export async function getSavedProCalculationByYear(
  userId: string,
  year: number
): Promise<SavedProCalculation | null> {
  try {
    // Query using the professionalCalculationsByUserAndYear GSI
    const response = await client.models.ProfessionalCalculation.list({
      filter: {
        userId: { eq: userId },
        calculationYear: { eq: year },
      },
      limit: 1,
    });

    if (response.data && response.data.length > 0) {
      return response.data[0] as unknown as SavedProCalculation;
    }

    return null;
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedProCalculation] getSavedProCalculationByYear error:', error);
    }
    throw error;
  }
}

/**
 * Map ProCalculatorFormValues and ProCalculatorResults to API input
 *
 * Handles JSON serialization for complex fields
 */
function mapFormValuesToApiInput(
  formValues: ProCalculatorFormValues,
  results: ProCalculatorResults
): Omit<CreateSavedProCalculationInput, 'userId' | 'calculationYear' | 'version'> {
  // Extract monthly consumption values (12 months)
  const monthlyConsumption = formValues.months.map((month) => month.consumption);

  // Extract tariff per month (12 months)
  const tariffPerMonth = {
    january: formValues.months[0].tariff,
    february: formValues.months[1].tariff,
    march: formValues.months[2].tariff,
    april: formValues.months[3].tariff,
    may: formValues.months[4].tariff,
    june: formValues.months[5].tariff,
    july: formValues.months[6].tariff,
    august: formValues.months[7].tariff,
    september: formValues.months[8].tariff,
    october: formValues.months[9].tariff,
    november: formValues.months[10].tariff,
    december: formValues.months[11].tariff,
  };

  // Extract hourly pattern per month (12 months)
  const hourlyPatternPerMonth = {
    january: formValues.months[0].hourlyPattern,
    february: formValues.months[1].hourlyPattern,
    march: formValues.months[2].hourlyPattern,
    april: formValues.months[3].hourlyPattern,
    may: formValues.months[4].hourlyPattern,
    june: formValues.months[5].hourlyPattern,
    july: formValues.months[6].hourlyPattern,
    august: formValues.months[7].hourlyPattern,
    september: formValues.months[8].hourlyPattern,
    october: formValues.months[9].hourlyPattern,
    november: formValues.months[10].hourlyPattern,
    december: formValues.months[11].hourlyPattern,
  };

  // Extract invoice parameters (11 parameters × 12 months × 4 values)
  const valuesFromBasic: Record<string, Record<string, any>> = {};
  const valuesFromInvoice: Record<string, Record<string, any>> = {};
  const valuesOverridden: Record<string, Record<string, any>> = {};
  const valuesFinal: Record<string, Record<string, any>> = {};

  const monthNames = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];

  formValues.months.forEach((month, index) => {
    const monthName = monthNames[index];

    // Convert MonthlyInvoiceParameters to flattened structure
    valuesFromBasic[monthName] = {
      energyElectricCost: month.parameters.energyElectricCost.valueFromBasic,
      handlingFee: month.parameters.handlingFee.valueFromBasic,
      networkFixedFee: month.parameters.networkFixedFee.valueFromBasic,
      transitionFee: month.parameters.transitionFee.valueFromBasic,
      qualityFee: month.parameters.qualityFee.valueFromBasic,
      networkVariableFee: month.parameters.networkVariableFee.valueFromBasic,
      ozeFee: month.parameters.ozeFee.valueFromBasic,
      cogenerationFee: month.parameters.cogenerationFee.valueFromBasic,
      subscriptionFee: month.parameters.subscriptionFee.valueFromBasic,
      powerFee: month.parameters.powerFee.valueFromBasic,
    };

    valuesFromInvoice[monthName] = {
      energyElectricCost: month.parameters.energyElectricCost.valueFromInvoice,
      handlingFee: month.parameters.handlingFee.valueFromInvoice,
      networkFixedFee: month.parameters.networkFixedFee.valueFromInvoice,
      transitionFee: month.parameters.transitionFee.valueFromInvoice,
      qualityFee: month.parameters.qualityFee.valueFromInvoice,
      networkVariableFee: month.parameters.networkVariableFee.valueFromInvoice,
      ozeFee: month.parameters.ozeFee.valueFromInvoice,
      cogenerationFee: month.parameters.cogenerationFee.valueFromInvoice,
      subscriptionFee: month.parameters.subscriptionFee.valueFromInvoice,
      powerFee: month.parameters.powerFee.valueFromInvoice,
    };

    valuesOverridden[monthName] = {
      energyElectricCost: month.parameters.energyElectricCost.valueOverridden,
      handlingFee: month.parameters.handlingFee.valueOverridden,
      networkFixedFee: month.parameters.networkFixedFee.valueOverridden,
      transitionFee: month.parameters.transitionFee.valueOverridden,
      qualityFee: month.parameters.qualityFee.valueOverridden,
      networkVariableFee: month.parameters.networkVariableFee.valueOverridden,
      ozeFee: month.parameters.ozeFee.valueOverridden,
      cogenerationFee: month.parameters.cogenerationFee.valueOverridden,
      subscriptionFee: month.parameters.subscriptionFee.valueOverridden,
      powerFee: month.parameters.powerFee.valueOverridden,
    };

    valuesFinal[monthName] = {
      energyElectricCost: month.parameters.energyElectricCost.valueFinal,
      handlingFee: month.parameters.handlingFee.valueFinal,
      networkFixedFee: month.parameters.networkFixedFee.valueFinal,
      transitionFee: month.parameters.transitionFee.valueFinal,
      qualityFee: month.parameters.qualityFee.valueFinal,
      networkVariableFee: month.parameters.networkVariableFee.valueFinal,
      ozeFee: month.parameters.ozeFee.valueFinal,
      cogenerationFee: month.parameters.cogenerationFee.valueFinal,
      subscriptionFee: month.parameters.subscriptionFee.valueFinal,
      powerFee: month.parameters.powerFee.valueFinal,
    };
  });

  // CRITICAL: GraphQL JSON fields require stringified JSON
  return {
    distributor: formValues.distributor,
    monthlyConsumption: JSON.stringify(monthlyConsumption) as any,
    tariffPerMonth: JSON.stringify(tariffPerMonth) as any,
    hourlyPatternPerMonth: JSON.stringify(hourlyPatternPerMonth) as any,
    valuesFromBasic: JSON.stringify(valuesFromBasic) as any,
    valuesFromInvoice: JSON.stringify(valuesFromInvoice) as any,
    valuesOverridden: JSON.stringify(valuesOverridden) as any,
    valuesFinal: JSON.stringify(valuesFinal) as any,
    simulationResults: JSON.stringify(results.simulations) as any,
    recommendedTariff: results.recommendedTariff,
    potentialSavings: results.potentialSavings,
    savedCalculationId: undefined, // Optional: reference to SavedCalculation (Phase 2)
  };
}

/**
 * Create or update a saved professional calculation for the current year
 *
 * Business Logic:
 * 1. Check if calculation exists for current year
 * 2. If exists: UPDATE with incremented version
 * 3. If not exists: CREATE new calculation
 *
 * @param formValues - Professional calculator form values
 * @param results - Professional calculator results
 * @returns Created or updated calculation
 */
export async function saveProCalculationForCurrentYear(
  formValues: ProCalculatorFormValues,
  results: ProCalculatorResults
): Promise<{ calculation: SavedProCalculation; isUpdate: boolean }> {
  try {
    const userId = await getCurrentUserId();
    const currentYear = new Date().getFullYear();

    // Check if calculation already exists for this year
    const existingCalculation = await getSavedProCalculationByYear(userId, currentYear);

    const apiInput = mapFormValuesToApiInput(formValues, results);

    if (existingCalculation && existingCalculation.id) {
      // UPDATE existing calculation
      const updateInput: UpdateSavedProCalculationInput = {
        id: existingCalculation.id,
        ...apiInput,
        version: existingCalculation.version + 1, // Optimistic locking
      };

      const response = await client.models.ProfessionalCalculation.update(updateInput as any);

      if (!response.data) {
        // Development only logging
        if (import.meta.env.DEV) {
          console.error('[SavedProCalculation] saveProCalculationForCurrentYear update: no response data');
        }
        throw new Error('Nie otrzymano odpowiedzi z serwera podczas aktualizacji');
      }

      return {
        calculation: response.data as unknown as SavedProCalculation,
        isUpdate: true,
      };
    }

    // CREATE new calculation
    const createInput: CreateSavedProCalculationInput = {
      userId,
      calculationYear: currentYear,
      ...apiInput,
      version: 1, // Initial version
    };

    const response = await client.models.ProfessionalCalculation.create(createInput as any);

    if (!response.data) {
      // Development only logging
      if (import.meta.env.DEV) {
        console.error('[SavedProCalculation] saveProCalculationForCurrentYear create: no response data');
      }
      throw new Error('Nie otrzymano odpowiedzi z serwera podczas tworzenia');
    }

    return {
      calculation: response.data as unknown as SavedProCalculation,
      isUpdate: false,
    };
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedProCalculation] saveProCalculationForCurrentYear error:', error);
    }
    throw error;
  }
}

/**
 * Get a single saved professional calculation by ID
 */
export async function getSavedProCalculation(id: string): Promise<SavedProCalculation | null> {
  try {
    const response = await client.models.ProfessionalCalculation.get({ id });
    return response.data as unknown as SavedProCalculation;
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedProCalculation] getSavedProCalculation error:', error);
    }
    throw error;
  }
}

/**
 * List all saved professional calculations for the authenticated user
 */
export async function listSavedProCalculations(options?: {
  limit?: number;
  nextToken?: string;
}): Promise<{ items: SavedProCalculation[]; nextToken?: string }> {
  try {
    const response = await client.models.ProfessionalCalculation.list({
      limit: options?.limit || 20,
      nextToken: options?.nextToken,
    });

    return {
      items: (response.data || []) as unknown as SavedProCalculation[],
      nextToken: response.nextToken || undefined,
    };
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedProCalculation] listSavedProCalculations error:', error);
    }
    throw error;
  }
}

/**
 * Update a saved professional calculation
 *
 * @param input - Update input with partial fields
 * @returns Updated calculation
 */
export async function updateSavedProCalculation(
  input: UpdateSavedProCalculationInput
): Promise<SavedProCalculation> {
  try {
    const response = await client.models.ProfessionalCalculation.update(input as any);

    if (!response.data) {
      // Development only logging
      if (import.meta.env.DEV) {
        console.error('[SavedProCalculation] updateSavedProCalculation: no response data');
      }
      throw new Error('Nie udało się zaktualizować kalkulacji');
    }

    return response.data as unknown as SavedProCalculation;
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedProCalculation] updateSavedProCalculation error:', error);
    }
    throw error;
  }
}

/**
 * Delete a saved professional calculation
 *
 * GDPR compliant: Users can delete their data at any time
 */
export async function deleteSavedProCalculation(id: string): Promise<void> {
  try {
    await client.models.ProfessionalCalculation.delete({ id });
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedProCalculation] deleteSavedProCalculation error:', error);
    }
    throw error;
  }
}

/**
 * Parse saved professional calculation back to form values and results
 *
 * CRITICAL: GraphQL JSON fields are returned as strings and must be parsed
 *
 * @param saved - Saved professional calculation from API
 * @returns Form values and results
 */
export function parseSavedProCalculation(saved: SavedProCalculation): {
  formValues: ProCalculatorFormValues;
  results: ProCalculatorResults;
} {
  // Parse JSON fields
  const monthlyConsumption = JSON.parse(saved.monthlyConsumption as any) as number[];
  const tariffPerMonth = JSON.parse(saved.tariffPerMonth as any) as Record<string, string>;
  const hourlyPatternPerMonth = JSON.parse(saved.hourlyPatternPerMonth as any) as Record<
    string,
    string
  >;
  const valuesFromBasic = JSON.parse(saved.valuesFromBasic as any) as Record<
    string,
    Record<string, number | null>
  >;
  const valuesFromInvoice = JSON.parse(saved.valuesFromInvoice as any) as Record<
    string,
    Record<string, number | null>
  >;
  const valuesOverridden = JSON.parse(saved.valuesOverridden as any) as Record<
    string,
    Record<string, number | null>
  >;
  const valuesFinal = JSON.parse(saved.valuesFinal as any) as Record<
    string,
    Record<string, number>
  >;
  const simulationResults = JSON.parse(saved.simulationResults as any);

  // Map back to MonthlyConfiguration array (12 months)
  const monthNames = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];

  const months = monthNames.map((monthName, index) => {
    const monthConfig: MonthlyConfiguration = {
      tariff: tariffPerMonth[monthName] as any,
      consumption: monthlyConsumption[index],
      hourlyPattern: hourlyPatternPerMonth[monthName] as any,
      parameters: {
        energyElectricCost: {
          valueFromBasic: valuesFromBasic[monthName]?.energyElectricCost ?? null,
          valueFromInvoice: valuesFromInvoice[monthName]?.energyElectricCost ?? null,
          valueOverridden: valuesOverridden[monthName]?.energyElectricCost ?? null,
          valueFinal: valuesFinal[monthName]?.energyElectricCost ?? 0,
        },
        handlingFee: {
          valueFromBasic: valuesFromBasic[monthName]?.handlingFee ?? null,
          valueFromInvoice: valuesFromInvoice[monthName]?.handlingFee ?? null,
          valueOverridden: valuesOverridden[monthName]?.handlingFee ?? null,
          valueFinal: valuesFinal[monthName]?.handlingFee ?? 0,
        },
        networkFixedFee: {
          valueFromBasic: valuesFromBasic[monthName]?.networkFixedFee ?? null,
          valueFromInvoice: valuesFromInvoice[monthName]?.networkFixedFee ?? null,
          valueOverridden: valuesOverridden[monthName]?.networkFixedFee ?? null,
          valueFinal: valuesFinal[monthName]?.networkFixedFee ?? 0,
        },
        transitionFee: {
          valueFromBasic: valuesFromBasic[monthName]?.transitionFee ?? null,
          valueFromInvoice: valuesFromInvoice[monthName]?.transitionFee ?? null,
          valueOverridden: valuesOverridden[monthName]?.transitionFee ?? null,
          valueFinal: valuesFinal[monthName]?.transitionFee ?? 0,
        },
        qualityFee: {
          valueFromBasic: valuesFromBasic[monthName]?.qualityFee ?? null,
          valueFromInvoice: valuesFromInvoice[monthName]?.qualityFee ?? null,
          valueOverridden: valuesOverridden[monthName]?.qualityFee ?? null,
          valueFinal: valuesFinal[monthName]?.qualityFee ?? 0,
        },
        networkVariableFee: {
          valueFromBasic: valuesFromBasic[monthName]?.networkVariableFee ?? null,
          valueFromInvoice: valuesFromInvoice[monthName]?.networkVariableFee ?? null,
          valueOverridden: valuesOverridden[monthName]?.networkVariableFee ?? null,
          valueFinal: valuesFinal[monthName]?.networkVariableFee ?? 0,
        },
        ozeFee: {
          valueFromBasic: valuesFromBasic[monthName]?.ozeFee ?? null,
          valueFromInvoice: valuesFromInvoice[monthName]?.ozeFee ?? null,
          valueOverridden: valuesOverridden[monthName]?.ozeFee ?? null,
          valueFinal: valuesFinal[monthName]?.ozeFee ?? 0,
        },
        cogenerationFee: {
          valueFromBasic: valuesFromBasic[monthName]?.cogenerationFee ?? null,
          valueFromInvoice: valuesFromInvoice[monthName]?.cogenerationFee ?? null,
          valueOverridden: valuesOverridden[monthName]?.cogenerationFee ?? null,
          valueFinal: valuesFinal[monthName]?.cogenerationFee ?? 0,
        },
        subscriptionFee: {
          valueFromBasic: valuesFromBasic[monthName]?.subscriptionFee ?? null,
          valueFromInvoice: valuesFromInvoice[monthName]?.subscriptionFee ?? null,
          valueOverridden: valuesOverridden[monthName]?.subscriptionFee ?? null,
          valueFinal: valuesFinal[monthName]?.subscriptionFee ?? 0,
        },
        powerFee: {
          valueFromBasic: valuesFromBasic[monthName]?.powerFee ?? null,
          valueFromInvoice: valuesFromInvoice[monthName]?.powerFee ?? null,
          valueOverridden: valuesOverridden[monthName]?.powerFee ?? null,
          valueFinal: valuesFinal[monthName]?.powerFee ?? 0,
        },
      },
    };

    return monthConfig;
  });

  const formValues: ProCalculatorFormValues = {
    calculationYear: saved.calculationYear,
    distributor: saved.distributor,
    handlingFeeType: 'REGULATED', // Default to regulated (old saved calculations don't have this)
    tariffCategory: 'HOUSEHOLD', // Default to household (old saved calculations don't have this)
    months: months as any, // TypeScript tuple type [12 items]
  };

  const results: ProCalculatorResults = {
    input: formValues,
    simulations: simulationResults,
    recommendedTariff: saved.recommendedTariff as any,
    potentialSavings: saved.potentialSavings,
    // PHASE 2 ENHANCEMENT: Calculate savingsPercent from simulation results
    // Formula: (potentialSavings / baselineCost) * 100
    // Requires defining baseline: worst tariff, current tariff, or user-provided current cost
    // Phase 1 MVP: Display absolute savings (PLN) only, percentage not shown in UI
    savingsPercent: 0,
    calculatedAt: new Date(saved.updatedAt),
  };

  return { formValues, results };
}
